import type { SweetAlertIcon, SweetAlertResult } from 'sweetalert2';
import Swal from 'sweetalert2/dist/sweetalert2.js';

class SwalManager {
	public static success(message: string) {
		return this.fire(message, 'success');
	}

	public static successToast(message: string) {
		return this.toastFire('Success', message, 'success');
	}

	public static error(message: string) {
		return this.fire(message, 'error');
	}

	public static errorToast(message: string) {
		return this.toastFire('Error', message, 'error');
	}

	public static warning(message: string) {
		return this.fire(message, 'warning');
	}

	public static info(message: string) {
		return this.fire(message, 'info');
	}

	public static infoToast(message: string) {
		return this.toastFire('Info', message, 'info');
	}

	public static question(message: string) {
		return this.fire(message, 'question');
	}

	public static confirmation(title: string, message: string) {
		return Swal.fire({
			title: title,
			text: message,
			showDenyButton: true,
			confirmButtonText: 'Yes',
			denyButtonText: 'Close',
			allowOutsideClick: false,
			reverseButtons: true,
			confirmButtonColor: '#dc3741',
			denyButtonColor: '#7066e0',
			customClass: {
				container: 'swal-container',
			},
		});
	}

	public static unsavedChanges() {
		return Swal.fire({
			title: 'Unsaved Changes',
			text: 'You have unsaved changes !',
			showDenyButton: true,
			confirmButtonText: 'Leave',
			denyButtonText: 'Stay',
			buttonsStyling: true,
			allowOutsideClick: false,
			customClass: {
				confirmButton: 'btn btn-danger',
				denyButton: 'btn btn-success',
			},
		});
	}

	public static viewHTML(html: string) {
		return Swal.fire({
			html: html,
			showDenyButton: false,
			showConfirmButton: false,
			backdrop: true,
			allowOutsideClick: true,
		});
	}

	public static async insertText(title: string): Promise<SweetAlertResult> {
		const result: SweetAlertResult = await Swal.fire({
			title: title,
			input: 'text',
			inputPlaceholder: 'Enter your text',
			allowOutsideClick: true,
			backdrop: true,
			allowEscapeKey: true,
			showDenyButton: true,
			inputValidator: (value) => {
				if (!value) {
					return 'This field is required';
				}
				return null;
			},
		});

		return result;
	}

	private static successButton: string = 'btn btn-primary';
	private static infoButton: string = 'btn fw-semobold btn-light-primary';
	private static errorButton: string = 'btn fw-semobold btn-light-danger';

	private static fire(message: string, icon: SweetAlertIcon) {
		return Swal.fire({
			text: message,
			icon: icon,
			buttonsStyling: false,
			confirmButtonText: 'Okay',
			heightAuto: false,
			customClass: {
				container: 'swal-container',
				confirmButton:
					icon === 'success'
						? this.successButton
						: icon === 'info'
						? this.infoButton
						: icon === 'error'
						? this.errorButton
						: 'btn btn-primary',
			},
		});
	}

	private static toastFire(
		title: string,
		message: string,
		icon: SweetAlertIcon
	) {
		return Swal.fire({
			toast: true,
			title: title,
			text: message,
			icon: icon,
			customClass: {
				container: 'swal2-top',
			},
			position: 'top-right',
			timer: 2000,
			timerProgressBar: true,
			showConfirmButton: false,
		});
	}
}

export default SwalManager;
